import { useContext, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { FilterValue } from 'antd/lib/table/interface';
import { useDispatch, useSelector } from 'react-redux';
import { produce } from 'immer';
import {
    IListFilter, IListRequest, TListFilter, TListSorters,
} from '../../types/api/requests';
import { useApiFetch } from '../api/useApiFetch';
import { ITableCardExtra, ITableLayout } from '../../types/layouts/table';
import { IListResponse, IListResponseSummary, IListResponseTableParams } from '../../types/api/responses';
import { tableStateSelector } from '../../redux/selectors/tables';
import { setFilters as setFiltersAction, setSorters as setSortersAction, updateDataSource } from '../../redux/actions/tables';
import PusherService from '../../services/pusher';
import { setShouldUpdate } from '../../redux/actions/layouts';
import { LayoutContext } from '../../context/layout';

type TTableFilter = Record<string, FilterValue | null>;

export const useDataSource = ({
    columns, dataLink, pageSize: defaultPageSize, name, predefinedFilters, indexKey,
}: ITableLayout) => {
    const [cardExtra, setCardExtra] = useState<ITableCardExtra>();
    const [dataSource, setDataSource] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(1);
    const [readyToFetch, setReadyToFetch] = useState(false);
    const [tableParams, setTableParams] = useState<IListResponseTableParams>();
    const [summary, setSummary] = useState<IListResponseSummary>();
    const tableState = useSelector(tableStateSelector(name));
    const [searchParams, setSearchParams] = useSearchParams();
    const [defaultFiltersProccessed, setDefaultFiltersProccessed] = useState(false);
    const [defaultSortersProccessed, setDefaultSortersProccessed] = useState(false);
    const layoutContext = useContext(LayoutContext);

    const page = parseInt(searchParams.get('page') || '1', 10);
    const pageSize = searchParams.get('pageSize') ? parseInt(searchParams.get('pageSize') as string, 10) : defaultPageSize;
    const search = searchParams.get('search') || undefined;

    const api = useApiFetch();
    const { objectName } = useParams();
    const dispatch = useDispatch();
    const pusher = new PusherService();

    const filters = tableState?.filters;
    const sorters = tableState?.sorters;

    const setFilters = (f: TListFilter) => {
        dispatch(setFiltersAction(name, f));
    };

    const setSorters = (s: TListSorters) => {
        dispatch(setSortersAction(name, s));
    };

    const fetchDataSource = async (silent = false) => {
        api.cancelRequest();

        if (readyToFetch) {
            if (!silent) {
                setLoading(true);
            }

            const body: IListRequest = {
                filters,
                sorters,
                count: pageSize,
                search,
            };

            const { data } = await api.fetchData<IListResponse>(dataLink ?? `/${objectName}/list?page=${page}`, 'post', true, body);

            if (Array.isArray(data?.list)) {
                if (typeof indexKey === 'string') {
                    // @ts-ignore
                    data.list = data.list.map((el: any, index) => {
                        el[indexKey] = index + 1;
                        return el;
                    });
                }
                setDataSource(data?.list || []);
            }

            setCardExtra(data?.cardExtra);
            setTotal(data?.total || 1);
            setTableParams(data?.tableParams);
            setSummary(data?.summary);

            if (data?.list.length === 0) {
                searchParams.set('page', '1');
                setSearchParams(searchParams);
            }

            setLoading(false);

            dispatch(updateDataSource(name, false));
        }
    };

    const getDefaultFilters = () => {
        if (typeof filters === 'undefined' && Array.isArray(predefinedFilters)) {
            setFilters(predefinedFilters);
        }
        setDefaultFiltersProccessed(true);
    };

    const getDefaultSorters = () => {
        if (typeof sorters === 'undefined') {
            const defaultSorters: TListSorters = [];

            columns.forEach((column) => {
                if (typeof column.dataIndex === 'string' && typeof column.defaultSortOrder === 'string') {
                    defaultSorters.push({
                        column: column.dataIndex,
                        desc: column.defaultSortOrder === 'descend',
                    });
                }
            });

            setSorters(defaultSorters);
        }

        setDefaultSortersProccessed(true);
    };

    const onFilterChange = (filter: IListFilter) => {
        const nextState = produce(filters || [], (draft = []) => {
            const index = draft?.findIndex((f) => f.column === filter.column);
            if (typeof index === 'number' && draft?.[index] instanceof Object) {
                if (filter.values.length > 0) {
                    draft[index] = filter;
                } else {
                    draft.splice(index, 1);
                }
            } else if (filter.values.length > 0) {
                draft.push(filter);
            }
        });
        setFilters(nextState);
    };

    const onFiltersChange = (f: TListFilter | TTableFilter) => {
        setFilters(Array.isArray(f) ? f : []);
    };

    const onPageChange = (p: number, pZ: number) => {
        searchParams.set('page', p.toString());
        searchParams.set('pageSize', pZ.toString());
        setSearchParams(searchParams);
    };

    const onDNDropSortChange = async (newDs: any[], result: { replacement: any; target: any; }) => {
        setDataSource(newDs);
        setLoading(true);
        await api.fetchData(`/${objectName}/dndSort`, 'post', true, result);
        fetchDataSource();
    };

    const onSearch = (searchValue: string) => {
        if (searchValue.length === 0) {
            searchParams.delete('search');
        } else {
            searchParams.set('search', searchValue);
        }
        searchParams.set('page', '1');
        setSearchParams(searchParams);
    };

    useEffect(() => {
        fetchDataSource();
        return () => {
            setDataSource([]);
        };
    }, [page, filters, sorters, readyToFetch, pageSize, search]);

    useEffect(() => {
        getDefaultSorters();
        getDefaultFilters();
    }, [objectName]);

    useEffect(() => {
        setReadyToFetch(defaultFiltersProccessed && defaultSortersProccessed);
    }, [defaultFiltersProccessed, defaultSortersProccessed]);

    useEffect(() => {
        if (readyToFetch) {
            pusher.open(name);
            pusher.registerMessageListener('table-updated', () => {
                dispatch(updateDataSource(name));
            });
            pusher.registerMessageListener('layout-updated', () => {
                dispatch(setShouldUpdate(layoutContext.uuid, true));
            });
        }
        return () => {
            pusher.close();
        };
    }, [readyToFetch]);

    useEffect(() => {
        if (tableState?.updateDataSource && !loading) {
            fetchDataSource(true);
        }
    }, [tableState?.updateDataSource]);

    return {
        cardExtra,
        currentPage: page,
        dataSource,
        loading,
        total,
        filters,
        sorters,
        updateDataSource: fetchDataSource,
        onFilterChange,
        onFiltersChange,
        onSortersChange: setSorters,
        setLoading,
        tableParams,
        onDNDropSortChange,
        summary,
        onPageChange,
        pageSize,
        onSearch,
        search,
    };
};
